import { isNumeric } from 'utils/misc';
import { logger } from 'utils/logger';
import { DivisionIdsToPolygonsNames } from 'screens/WidgetGenerator/reportGeneratorComponents/types';

const LONG_POLYGON_NAME_EXTENSION = '...';

interface MaxCharactersLimitProps {
  maxCharactersLimit: number;
}

interface SinglePolygonNameAbbreviatingFunctionProps extends MaxCharactersLimitProps {
  polygonName: string;
}

interface AbbreviatePolygonNamesProps extends MaxCharactersLimitProps {
  divisionIdsToPolygonNames: DivisionIdsToPolygonsNames;
}

interface OverExtendedPolygonNameExistProps extends MaxCharactersLimitProps {
  polygonNames: string[];
}

interface AbbreviationIterationProps {
  firstWord: string;
  secondWord: string;
  restOfWords: string[];
}

interface SecondAbbreviationIterationProps extends AbbreviationIterationProps {
  abbreviatedPolygonName: string;
}

const trimLastCharactersOfPolygonName = (props: SinglePolygonNameAbbreviatingFunctionProps) => {
  const { polygonName, maxCharactersLimit } = props;
  return `${polygonName.substring(0, maxCharactersLimit)}${LONG_POLYGON_NAME_EXTENSION}`;
};

const firstAbbreviationIteration = (props: AbbreviationIterationProps) => {
  const { firstWord, secondWord, restOfWords } = props;
  return isNumeric(firstWord)
    ? `${firstWord} ${secondWord[0]}.${restOfWords.join(' ')}`
    : `${firstWord[0]}.${[secondWord, ...restOfWords].join(' ')}`;
};

const secondAbbreviationIteration = (props: SecondAbbreviationIterationProps) => {
  const { firstWord, secondWord, restOfWords, abbreviatedPolygonName } = props;
  if (!isNumeric(firstWord) && restOfWords.length) {
    return `${firstWord[0]}.${secondWord[0]}.${restOfWords.join(' ')}`;
  }
  if (isNumeric(firstWord)) {
    if (restOfWords.length <= 1) {
      // Will be trimmed later on
      return abbreviatedPolygonName;
    }
    // Second word already abbreviated
    const thirdWord = restOfWords[0];
    return `${firstWord} ${secondWord[0]}.${thirdWord[0]}.${restOfWords.slice(1).join(' ')}`;
  }
  // Will be trimmed later on
  return abbreviatedPolygonName;
};

const abbreviateSinglePolygonName = (props: SinglePolygonNameAbbreviatingFunctionProps) => {
  const { polygonName, maxCharactersLimit } = props;
  const [firstWord, secondWord, ...restOfWords] = polygonName.split(' ');

  const abbreviatedPolygonName = firstAbbreviationIteration({ firstWord, secondWord, restOfWords });

  if (abbreviatedPolygonName.length <= maxCharactersLimit) {
    return abbreviatedPolygonName;
  }

  // If abbreviatedPolygonName is still not short enough - proceed with further abbreviation
  const newAbbreviatedPolygonName = secondAbbreviationIteration({
    firstWord,
    secondWord,
    restOfWords,
    abbreviatedPolygonName,
  });

  // Use the abbreviated name if it adheres to the character limit; otherwise, abbreviate it further
  return newAbbreviatedPolygonName.length > maxCharactersLimit
    ? trimLastCharactersOfPolygonName({ polygonName: newAbbreviatedPolygonName, maxCharactersLimit })
    : newAbbreviatedPolygonName;
};

const guaranteeUniquePolygonNames = (divisionIdsToAbbreviatedPolygonNames: DivisionIdsToPolygonsNames) => {
  const abbreviatedPolygonNames = Object.values(divisionIdsToAbbreviatedPolygonNames);
  const areAbbreviatedNamesUnique = new Set(abbreviatedPolygonNames).size === abbreviatedPolygonNames.length;
  if (areAbbreviatedNamesUnique) {
    return divisionIdsToAbbreviatedPolygonNames;
  }
  const duplicatePolygonNames = abbreviatedPolygonNames.filter(
    (name, index, polygonNames) => polygonNames.indexOf(name) !== index,
  );
  logger.info(
    `After abbreviating polygons' names, duplicate names were found, therefore a unique id was added for each
    polygon name. Duplicate names were: ${duplicatePolygonNames}`,
  );
  return Object.fromEntries(
    Object.entries(divisionIdsToAbbreviatedPolygonNames).map(([divisionId, polygonName], index) => [
      divisionId,
      `${polygonName} (${index + 1})`,
    ]),
  );
};

// See: https://www.notion.so/zencity/Surveys-Widget-Polygon-Names-Trimming-Logic-636d403d3fb2487791b2e8c3cfd0b8a1 for
// full logic details.
export const abbreviatePolygonNames = (props: AbbreviatePolygonNamesProps) => {
  const { divisionIdsToPolygonNames, maxCharactersLimit } = props;
  const abbreviatedPolygonNames: { [divisionId: number]: string } = {};

  Object.entries(divisionIdsToPolygonNames).forEach(([divisionId, polygonName]) => {
    if (polygonName.length <= maxCharactersLimit) {
      abbreviatedPolygonNames[Number(divisionId)] = polygonName;
    } else if (polygonName.split(' ').length === 1) {
      abbreviatedPolygonNames[Number(divisionId)] = trimLastCharactersOfPolygonName({
        polygonName,
        maxCharactersLimit,
      });
    } else {
      abbreviatedPolygonNames[Number(divisionId)] = abbreviateSinglePolygonName({ polygonName, maxCharactersLimit });
    }
  });
  return guaranteeUniquePolygonNames(abbreviatedPolygonNames);
};

export const overExtendedPolygonNameExist = (props: OverExtendedPolygonNameExistProps) => {
  const { polygonNames, maxCharactersLimit } = props;
  return !!polygonNames.find((polygonName) => polygonName.length > maxCharactersLimit);
};
